import * as types from '../mutations'
import AxiosInstance from '@/utils/axios.instances.js'
import AuthService from '@/services/auth'
import Permissions from '@/const/permissions'

const state = {
  me: {},
  scantrustToken: '',
  error: false,
  errorMessage: '',
  refreshingToken: false,
  pendingRequests: []
}

const getters = {
  scantrustAuthToken: (state) => state.scantrustToken,
  scantrustAuthError: (state) => state.error,
  scantrustAuthErrorMessage: (state) => state.errorMessage,

  profile: (state) => {
    return state.me
  },

  isAdmin: (state) => {
    return state.me.is_staff
  },

  language: (state) => {
    return state.me.language
  },

  isRemyCompany: (state) => {
    return state.me.plan && state.me.plan.id.indexOf('remycointreau') >= 0
  },

  isScantrustUser: (state) => {
    if (state.me) {
      return ~state.me.email.indexOf('@scantrust.com')
    }
    return false
  },

  hasLegacyPlan: (state) => {
    const legacyPlans = [
      'elabel-business-30',
      'elabel-business-50',
      'elabel-business-100',
      'elabel-premium',
      'elabel-freemium'
    ]
    if (state.me.plan && state.me.plan.id) {
      return legacyPlans.includes(state.me.plan.id)
    }
    return false
  },

  currentPlan: (state) => {
    if (state.me.plan && state.me.plan.id !== '@not-set') {
      return state.me.plan
    }
    if (state.me.company) {
      return { id: state.me.company.plan }
    }
  },

  isAvailable: (state, getters) => (feature) => {
    // Check against new plan feature. If plan do not exist, fall back to existing permission system.
    //
    if (!state.me.plan || !state.me.company) {
      return false
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      // New Plan Check
      return (state.me.plan.interface && state.me.plan.interface[feature]) || getters.isAdmin
    } else {
      // Old check
      if (!Permissions[state.me.company.plan] || getters.isAdmin) {
        return true
      }
      return Permissions[state.me.company.plan].features[feature] && Permissions[state.me.company.plan].features[feature].is_available
    }
  },

  hasLimit: (state, getters) => (feature) => {
    if (!state.me.plan || !state.me.company) {
      return false
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      if (getters.isScantrustUser) {
        return false
      } else {
        return (state.me.plan.interface && state.me.plan.interface[feature] && typeof state.me.plan.interface[feature] === 'number')
      }
    } else {
      // Old check
      if (!Permissions[state.me.company.plan] || getters.isScantrustUser) {
        return true
      }

      return Permissions[state.me.company.plan].features[feature] && Permissions[state.me.company.plan].features[feature].has_limit
    }
  },

  getLimit: (state, getters) => (feature) => {
    if (!state.me.plan || !state.me.company) {
      return 0
    }

    if (state.me.plan && state.me.plan.id !== '@not-set') {
      if (getters.isScantrustUser) {
        return true
      } else if (state.me.plan.interface && state.me.plan.interface[feature] && typeof state.me.plan.interface[feature] === 'number') {
        return state.me.plan.interface[feature]
      }
    } else {
      // Old check
      if (!Permissions[state.me.company.plan] || getters.isScantrustUser) {
        return 999
      }

      return Permissions[state.me.company.plan].features[feature] && Permissions[state.me.company.plan].features[feature].limit
    }
  },

  isRefreshingToken: (state) => {
    return state.refreshingToken
  },

  tokenRefreshRequests: (state) => {
    return state.pendingRequests
  },

  isAllowed: (state) => (action) => {
    if (!state.me || !state.me.permissions) return false

    const permissions = state.me.permissions

    return permissions?.includes(action) || state.me.is_staff
  }
}

const actions = {
  setScantrustAuthToken ({ commit }, token) {
    AxiosInstance.setSTAuthToken(token)
    commit(types.SCANTRUST_AUTH_SUCCESS, token)
  },

  async loadUserProfile ({ commit }) {
    const profile = await AuthService.loadProfile()
    commit(types.SET_USER_PROFILE, profile)
    return profile
  },

  scantrustAuthError ({ commit }, err) {
    commit(types.SCANTRUST_AUTH_ERROR, err)
  },

  setIsRefreshingToken ({ commit }, err) {
    commit(types.SET_IS_REFRESHING_TOKEN, err)
  },

  resetPendingRequest ({ commit }, err) {
    commit(types.RESET_PENDING_REQUEST, err)
  },

  addPendingRequest ({ commit }, err) {
    commit(types.ADD_PENDING_REQUEST, err)
  },

  processPendingRequest ({ commit }, err) {
    commit(types.PROCESS_PENDING_REQUEST, err)
  }
}

const mutations = {
  [types.SCANTRUST_AUTH_SUCCESS] (state, token) {
    state.scantrustToken = token
  },

  [types.SCANTRUST_AUTH_ERROR] (state, error) {
    if (error & error.status === 401) {
      state.error = true
      if (state.scantrustToken) {
        state.errorMessage = 'Invalid Token, please refresh your browser'
      } else {
        state.errorMessage = ' Authentication Token not provided'
      }
    }
  },

  [types.SET_USER_PROFILE] (state, profile) {
    state.me = profile
  },

  [types.SET_IS_REFRESHING_TOKEN] (state, isRefreshing) {
    state.refreshingToken = isRefreshing
  },

  [types.ADD_PENDING_REQUEST] (state, request) {
    state.pendingRequests = [...state.pendingRequests, request]
  },

  [types.RESET_PENDING_REQUEST] (state) {
    state.pendingRequests = []
  },

  [types.PROCESS_PENDING_REQUEST] (state, token) {
    state.pendingRequests.forEach((request) => {
      request.resolve(token)
    })
    state.pendingRequests = []
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
